"use client";

import { c as _c } from "react/compiler-runtime";
import { track } from "@opensea/analytics";
import { usePathname, useRouter } from "@opensea/navigation";
import { SidebarItem, SidebarItemAvatar, SidebarItemTitle, Sidebar as UISidebar } from "@opensea/next-ui-kit";
import { Separator, SidebarCollapsibleItem } from "@opensea/ui-kit";
import { ContactSupport, LibraryBooks, OpenSeaBrandLogo, OpenSeaHalloweenLogo, OpenSeaLetters, Settings } from "@opensea/ui-kit/icons";
import { useIsEmbeddedWallet } from "@opensea/wallet";
import { useTranslations } from "next-intl";
import { Fragment } from "react";
import { useStore } from "zustand";
import { useShallow } from "zustand/react/shallow";
import { useHideNavigationLayoutComponents } from "@/components/navigation/use-hide-navigation-layout-components";
import { useIntercom } from "@/components/support/IntercomProvider";
import { WhatsNewModalStore } from "@/components/whats-new/whats-new-modal-store";
import { BLOG_URL, DEVELOPER_DOCS_URL, DISCORD_URL, LEARN_URL, SUPPORT_URL, TAX_RESOURCES_URL } from "@/constants/constants";
import { useBasicProfile } from "@/hooks/api/use-basic-profile";
import { useIsHalloweenLogoEnabled } from "@/hooks/use-flags";
import { useTrackHomePageNavigation } from "@/hooks/use-track-home-page-navigation";
import { useExplicitEnterHoverGate } from "@/hooks/useExplicitEnterHoverGate";
import { useAddress } from "@/hooks/wallet";
import { useKeybindModalStore } from "@/stores/use-keybind-modal-store";
import { useLanguageModalStore } from "@/stores/use-language-modal-store";
import { useIsItemActive, useNavItems } from "../nav-items";
export function Sidebar() {
  const $ = _c(145);
  const navItems = useNavItems();
  const isItemActive = useIsItemActive();
  const trackHomePageNavigation = useTrackHomePageNavigation();
  const t = useTranslations("Sidebar");
  const router = useRouter();
  const {
    profile
  } = useBasicProfile();
  const connectedAddress = useAddress();
  const intercom = useIntercom();
  const hideNavigationLayoutComponents = useHideNavigationLayoutComponents();
  const isHalloweenLogoEnabled = useIsHalloweenLogoEnabled();
  const {
    setModalOpen: setLanguageModalOpen
  } = useLanguageModalStore(useShallow(_temp));
  const {
    setModalOpen: setShortcutModalOpen
  } = useKeybindModalStore(useShallow(_temp2));
  const setWhatsNewForceState = useStore(WhatsNewModalStore, _temp3);
  const setWhatsNewOverrideLastSeen = useStore(WhatsNewModalStore, _temp4);
  const isEmbeddedWallet = useIsEmbeddedWallet();
  const pathname = usePathname();
  let t0;
  if ($[0] !== pathname) {
    t0 = {
      side: "left",
      collapsedWidthPx: 52,
      resetKey: pathname
    };
    $[0] = pathname;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const {
    overlay,
    gated
  } = useExplicitEnterHoverGate(t0);
  if (hideNavigationLayoutComponents) {
    return null;
  }
  const t1 = isHalloweenLogoEnabled ? OpenSeaHalloweenLogo : OpenSeaBrandLogo;
  let t2;
  if ($[2] !== t1) {
    t2 = <SidebarItemAvatar icon={t1} size={30} />;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="contents *:translate-y-[2px]"><SidebarItemTitle><OpenSeaLetters width={97} /></SidebarItemTitle></div>;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t2 || $[6] !== trackHomePageNavigation) {
    t4 = <SidebarItem className="p-[3px]" href="/" interactive={false} onNavigationCompleted={trackHomePageNavigation}>{t2}{t3}</SidebarItem>;
    $[5] = t2;
    $[6] = trackHomePageNavigation;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== connectedAddress || $[9] !== isItemActive || $[10] !== navItems || $[11] !== profile) {
    let t6;
    if ($[13] !== connectedAddress || $[14] !== isItemActive || $[15] !== profile) {
      t6 = item => {
        const hasSubItems = item.subItems && item.subItems.length > 0;
        if (hasSubItems) {
          const active = item.subItems?.some(subItem => isItemActive(subItem));
          return <Fragment key={item.title}><SidebarCollapsibleItem active={active} aria-current={active ? "page" : undefined} content={item.subItems?.map(subItem_0 => {
              const subItemActive = isItemActive(subItem_0);
              return <SidebarItem active={subItemActive} aria-current={subItemActive ? "page" : undefined} data-testid={subItem_0["data-testid"]} href={subItem_0.href} key={subItem_0.title} onNavigationCompleted={subItem_0.onNavigationCompleted} onPointerOver={subItemActive ? undefined : subItem_0.prefetch} size="sm"><SidebarItemTitle size="sm">{subItem_0.title}</SidebarItemTitle></SidebarItem>;
            })} data-testid={item["data-testid"]} key={item.title}><SidebarItemAvatar aria-labelledby={`nav-item-${item.id ?? ""}`} rounded={true} {...item.id === "profile" && profile ? {
                src: profile.imageUrl,
                seed: connectedAddress
              } : {
                icon: active ? item.activeIcon : item.icon
              }} /><div className="contents" id={`nav-item-${item.id ?? ""}`}><SidebarItemTitle>{item.title}</SidebarItemTitle></div></SidebarCollapsibleItem></Fragment>;
        }
        const active_0 = isItemActive(item);
        return <Fragment key={item.title}><SidebarItem active={active_0} aria-current={active_0 ? "page" : undefined} data-testid={item["data-testid"]} href={item.href} onNavigationCompleted={item.onNavigationCompleted} onPointerOver={active_0 ? undefined : item.prefetch}><SidebarItemAvatar icon={active_0 ? item.activeIcon : item.icon} /><SidebarItemTitle>{item.title}</SidebarItemTitle></SidebarItem></Fragment>;
      };
      $[13] = connectedAddress;
      $[14] = isItemActive;
      $[15] = profile;
      $[16] = t6;
    } else {
      t6 = $[16];
    }
    t5 = navItems.map(t6);
    $[8] = connectedAddress;
    $[9] = isItemActive;
    $[10] = navItems;
    $[11] = profile;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <Separator className="my-1" />;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== t) {
    t7 = t("learn");
    $[18] = t;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== t7) {
    t8 = <SidebarItem href={LEARN_URL} size="sm"><SidebarItemTitle size="sm">{t7}</SidebarItemTitle></SidebarItem>;
    $[20] = t7;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== t) {
    t9 = t("helpCenter");
    $[22] = t;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== t9) {
    t10 = <SidebarItem href={SUPPORT_URL} size="sm"><SidebarItemTitle size="sm">{t9}</SidebarItemTitle></SidebarItem>;
    $[24] = t9;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== t) {
    t11 = t("blog");
    $[26] = t;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  if ($[28] !== t11) {
    t12 = <SidebarItem href={BLOG_URL} size="sm"><SidebarItemTitle size="sm">{t11}</SidebarItemTitle></SidebarItem>;
    $[28] = t11;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] !== t) {
    t13 = t("careers");
    $[30] = t;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  let t14;
  if ($[32] !== t13) {
    t14 = <SidebarItem href="/careers" size="sm"><SidebarItemTitle size="sm">{t13}</SidebarItemTitle></SidebarItem>;
    $[32] = t13;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  let t15;
  if ($[34] !== t) {
    t15 = t("taxResources");
    $[34] = t;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] !== t15) {
    t16 = <SidebarItem href={TAX_RESOURCES_URL} size="sm"><SidebarItemTitle size="sm">{t15}</SidebarItemTitle></SidebarItem>;
    $[36] = t15;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  let t17;
  if ($[38] !== t) {
    t17 = t("discord");
    $[38] = t;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  let t18;
  if ($[40] !== t17) {
    t18 = <SidebarItem href={DISCORD_URL} size="sm"><SidebarItemTitle size="sm">{t17}</SidebarItemTitle></SidebarItem>;
    $[40] = t17;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  let t19;
  if ($[42] !== t) {
    t19 = t("devDocs");
    $[42] = t;
    $[43] = t19;
  } else {
    t19 = $[43];
  }
  let t20;
  if ($[44] !== t19) {
    t20 = <SidebarItem href={DEVELOPER_DOCS_URL} size="sm"><SidebarItemTitle size="sm">{t19}</SidebarItemTitle></SidebarItem>;
    $[44] = t19;
    $[45] = t20;
  } else {
    t20 = $[45];
  }
  let t21;
  if ($[46] !== router || $[47] !== setWhatsNewForceState || $[48] !== setWhatsNewOverrideLastSeen) {
    t21 = () => {
      setWhatsNewOverrideLastSeen(new Date(0));
      setWhatsNewForceState("open");
      router.push("/");
    };
    $[46] = router;
    $[47] = setWhatsNewForceState;
    $[48] = setWhatsNewOverrideLastSeen;
    $[49] = t21;
  } else {
    t21 = $[49];
  }
  let t22;
  if ($[50] !== t) {
    t22 = t("whatsNew");
    $[50] = t;
    $[51] = t22;
  } else {
    t22 = $[51];
  }
  let t23;
  if ($[52] !== t22) {
    t23 = <SidebarItemTitle size="sm">{t22}</SidebarItemTitle>;
    $[52] = t22;
    $[53] = t23;
  } else {
    t23 = $[53];
  }
  let t24;
  if ($[54] !== t21 || $[55] !== t23) {
    t24 = <SidebarItem data-testid="sidebar-whats-new" onClick={t21} size="sm">{t23}</SidebarItem>;
    $[54] = t21;
    $[55] = t23;
    $[56] = t24;
  } else {
    t24 = $[56];
  }
  let t25;
  if ($[57] !== t10 || $[58] !== t12 || $[59] !== t14 || $[60] !== t16 || $[61] !== t18 || $[62] !== t20 || $[63] !== t24 || $[64] !== t8) {
    t25 = <>{t8}{t10}{t12}{t14}{t16}{t18}{t20}{t24}</>;
    $[57] = t10;
    $[58] = t12;
    $[59] = t14;
    $[60] = t16;
    $[61] = t18;
    $[62] = t20;
    $[63] = t24;
    $[64] = t8;
    $[65] = t25;
  } else {
    t25 = $[65];
  }
  let t26;
  if ($[66] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <SidebarItemAvatar icon={LibraryBooks} />;
    $[66] = t26;
  } else {
    t26 = $[66];
  }
  let t27;
  if ($[67] !== t) {
    t27 = t("resources");
    $[67] = t;
    $[68] = t27;
  } else {
    t27 = $[68];
  }
  let t28;
  if ($[69] !== t27) {
    t28 = <SidebarItemTitle>{t27}</SidebarItemTitle>;
    $[69] = t27;
    $[70] = t28;
  } else {
    t28 = $[70];
  }
  let t29;
  if ($[71] !== t25 || $[72] !== t28) {
    t29 = <SidebarCollapsibleItem content={t25}>{t26}{t28}</SidebarCollapsibleItem>;
    $[71] = t25;
    $[72] = t28;
    $[73] = t29;
  } else {
    t29 = $[73];
  }
  let t30;
  if ($[74] !== t) {
    t30 = t("settings.profile");
    $[74] = t;
    $[75] = t30;
  } else {
    t30 = $[75];
  }
  let t31;
  if ($[76] !== t30) {
    t31 = <SidebarItem data-testid="sidebar-settings-profile" href="/settings/profile" size="sm"><SidebarItemTitle size="sm">{t30}</SidebarItemTitle></SidebarItem>;
    $[76] = t30;
    $[77] = t31;
  } else {
    t31 = $[77];
  }
  let t32;
  if ($[78] !== t) {
    t32 = t("settings.walletLinking");
    $[78] = t;
    $[79] = t32;
  } else {
    t32 = $[79];
  }
  let t33;
  if ($[80] !== t32) {
    t33 = <SidebarItem href="/settings/wallet-linking" size="sm"><SidebarItemTitle size="sm">{t32}</SidebarItemTitle></SidebarItem>;
    $[80] = t32;
    $[81] = t33;
  } else {
    t33 = $[81];
  }
  let t34;
  if ($[82] !== t) {
    t34 = t("settings.emailNotifications");
    $[82] = t;
    $[83] = t34;
  } else {
    t34 = $[83];
  }
  let t35;
  if ($[84] !== t34) {
    t35 = <SidebarItem data-testid="sidebar-settings-email-notifications" href="/settings/email-notifications" size="sm"><SidebarItemTitle size="sm">{t34}</SidebarItemTitle></SidebarItem>;
    $[84] = t34;
    $[85] = t35;
  } else {
    t35 = $[85];
  }
  let t36;
  if ($[86] !== t) {
    t36 = t("settings.developer");
    $[86] = t;
    $[87] = t36;
  } else {
    t36 = $[87];
  }
  let t37;
  if ($[88] !== t36) {
    t37 = <SidebarItem data-testid="sidebar-settings-developer" href="/settings/developer" size="sm"><SidebarItemTitle size="sm">{t36}</SidebarItemTitle></SidebarItem>;
    $[88] = t36;
    $[89] = t37;
  } else {
    t37 = $[89];
  }
  let t38;
  if ($[90] !== t) {
    t38 = t("settings.verification");
    $[90] = t;
    $[91] = t38;
  } else {
    t38 = $[91];
  }
  let t39;
  if ($[92] !== t38) {
    t39 = <SidebarItem data-testid="sidebar-settings-verification" href="/settings/verification" size="sm"><SidebarItemTitle size="sm">{t38}</SidebarItemTitle></SidebarItem>;
    $[92] = t38;
    $[93] = t39;
  } else {
    t39 = $[93];
  }
  let t40;
  if ($[94] !== setLanguageModalOpen) {
    t40 = () => setLanguageModalOpen(true);
    $[94] = setLanguageModalOpen;
    $[95] = t40;
  } else {
    t40 = $[95];
  }
  let t41;
  if ($[96] !== t) {
    t41 = t("settings.language");
    $[96] = t;
    $[97] = t41;
  } else {
    t41 = $[97];
  }
  let t42;
  if ($[98] !== t41) {
    t42 = <SidebarItemTitle size="sm">{t41}</SidebarItemTitle>;
    $[98] = t41;
    $[99] = t42;
  } else {
    t42 = $[99];
  }
  let t43;
  if ($[100] !== t40 || $[101] !== t42) {
    t43 = <SidebarItem data-testid="sidebar-language" onClick={t40} size="sm">{t42}</SidebarItem>;
    $[100] = t40;
    $[101] = t42;
    $[102] = t43;
  } else {
    t43 = $[102];
  }
  let t44;
  if ($[103] !== setShortcutModalOpen) {
    t44 = () => setShortcutModalOpen(true);
    $[103] = setShortcutModalOpen;
    $[104] = t44;
  } else {
    t44 = $[104];
  }
  let t45;
  if ($[105] !== t) {
    t45 = t("settings.shortcuts");
    $[105] = t;
    $[106] = t45;
  } else {
    t45 = $[106];
  }
  let t46;
  if ($[107] !== t45) {
    t46 = <SidebarItemTitle size="sm">{t45}</SidebarItemTitle>;
    $[107] = t45;
    $[108] = t46;
  } else {
    t46 = $[108];
  }
  let t47;
  if ($[109] !== t44 || $[110] !== t46) {
    t47 = <SidebarItem onClick={t44} size="sm">{t46}</SidebarItem>;
    $[109] = t44;
    $[110] = t46;
    $[111] = t47;
  } else {
    t47 = $[111];
  }
  let t48;
  if ($[112] !== isEmbeddedWallet || $[113] !== t) {
    t48 = isEmbeddedWallet ? <SidebarItem href="/settings/embedded-wallet" size="sm"><SidebarItemTitle size="sm">{t("settings.embeddedWallet")}</SidebarItemTitle></SidebarItem> : null;
    $[112] = isEmbeddedWallet;
    $[113] = t;
    $[114] = t48;
  } else {
    t48 = $[114];
  }
  let t49;
  if ($[115] !== t31 || $[116] !== t33 || $[117] !== t35 || $[118] !== t37 || $[119] !== t39 || $[120] !== t43 || $[121] !== t47 || $[122] !== t48) {
    t49 = <>{t31}{t33}{t35}{t37}{t39}{t43}{t47}{t48}</>;
    $[115] = t31;
    $[116] = t33;
    $[117] = t35;
    $[118] = t37;
    $[119] = t39;
    $[120] = t43;
    $[121] = t47;
    $[122] = t48;
    $[123] = t49;
  } else {
    t49 = $[123];
  }
  let t50;
  if ($[124] === Symbol.for("react.memo_cache_sentinel")) {
    t50 = <SidebarItemAvatar icon={Settings} />;
    $[124] = t50;
  } else {
    t50 = $[124];
  }
  let t51;
  if ($[125] !== t) {
    t51 = t("settings.settings");
    $[125] = t;
    $[126] = t51;
  } else {
    t51 = $[126];
  }
  let t52;
  if ($[127] !== t51) {
    t52 = <SidebarItemTitle>{t51}</SidebarItemTitle>;
    $[127] = t51;
    $[128] = t52;
  } else {
    t52 = $[128];
  }
  let t53;
  if ($[129] !== t49 || $[130] !== t52) {
    t53 = <SidebarCollapsibleItem content={t49} data-testid="sidebar-settings">{t50}{t52}</SidebarCollapsibleItem>;
    $[129] = t49;
    $[130] = t52;
    $[131] = t53;
  } else {
    t53 = $[131];
  }
  let t54;
  if ($[132] !== intercom || $[133] !== t) {
    t54 = intercom.isEnabled ? <SidebarItem onClick={() => {
      track("Intercom support click", {
        inApp: true
      });
      intercom.boot();
      intercom.show();
    }}><SidebarItemAvatar icon={ContactSupport} /><SidebarItemTitle>{t("support")}</SidebarItemTitle></SidebarItem> : <SidebarItem href={SUPPORT_URL} onClick={_temp5}><SidebarItemAvatar icon={ContactSupport} /><SidebarItemTitle>{t("support")}</SidebarItemTitle></SidebarItem>;
    $[132] = intercom;
    $[133] = t;
    $[134] = t54;
  } else {
    t54 = $[134];
  }
  let t55;
  if ($[135] !== gated || $[136] !== t29 || $[137] !== t4 || $[138] !== t5 || $[139] !== t53 || $[140] !== t54) {
    t55 = <UISidebar aria-label="Main" className="scrollbar-hidden group overflow-y-scroll *:min-w-9" disableExpand={gated}>{t4}{t5}{t6}{t29}{t53}{t54}</UISidebar>;
    $[135] = gated;
    $[136] = t29;
    $[137] = t4;
    $[138] = t5;
    $[139] = t53;
    $[140] = t54;
    $[141] = t55;
  } else {
    t55 = $[141];
  }
  let t56;
  if ($[142] !== overlay || $[143] !== t55) {
    t56 = <>{overlay}{t55}</>;
    $[142] = overlay;
    $[143] = t55;
    $[144] = t56;
  } else {
    t56 = $[144];
  }
  return t56;
}
function _temp5() {
  track("Intercom support click", {
    inApp: false
  });
}
function _temp4(state_2) {
  return state_2.setOverrideLastSeen;
}
function _temp3(state_1) {
  return state_1.setForceState;
}
function _temp2(state_0) {
  return {
    setModalOpen: state_0.setModalOpen
  };
}
function _temp(state) {
  return {
    setModalOpen: state.setModalOpen
  };
}